/**
 * Presentation helpers for the admin Settings option lists (Contractor Types,
 * Cluster Regions, Stations) — the string arrays stored on
 * `settings.branding.{contractorType,clusterRegion,station}Options`.
 */

// `sensitivity: 'base'` so casing (and accents) do not split the ordering into
// a capitalised block followed by a lowercase one; `numeric: true` so
// "Station 2" precedes "Station 10". Built once — constructing a Collator is
// the expensive part, comparing with it is cheap.
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/**
 * Returns the list ordered alphabetically, case-insensitively and with embedded
 * numbers compared as numbers.
 *
 * Copies before sorting: callers pass a list derived from the react-query
 * `['settings']` cache, which every other consumer shares by reference, so an
 * in-place sort would reorder the vendor dropdowns and filter chips as a side
 * effect of merely rendering the editor.
 *
 * Entries differing only by case compare equal under `sensitivity: 'base'`;
 * `Array.prototype.sort` is stable, so both survive in their original relative
 * order. Sorting never adds, drops or rewrites an entry — deduplication is
 * `normalizeStringList`'s job.
 */
export function sortOptionList(list: string[]): string[] {
  return [...list].sort(collator.compare);
}

/**
 * True when `value` should stay visible for the given search box contents.
 * Case-insensitive substring match; a blank or whitespace-only query matches
 * everything, so clearing the box restores the full list.
 */
export function matchesOptionSearch(value: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return value.toLowerCase().includes(needle);
}

/**
 * The subset of editor rows to render for the current search box contents.
 *
 * `exemptIds` names rows the filter must not hide — the rows added since the
 * search box was last touched. A row is empty the moment it is added, so it
 * matches no non-blank query; without the exemption, pressing "Add" while a
 * filter was active would appear to do nothing at all.
 *
 * Filtering is presentation only. The editor keeps editing and deleting against
 * its full row list, so a hidden row is never silently dropped from the saved
 * value.
 */
export function visibleOptionRows<T extends { id: number; value: string }>(
  rows: readonly T[],
  query: string,
  exemptIds: ReadonlySet<number>,
): T[] {
  return rows.filter(
    (row) => exemptIds.has(row.id) || matchesOptionSearch(row.value.trim(), query),
  );
}

/**
 * True when the edited list holds different entries from the stored one — what
 * the "Unsaved changes" badge reports.
 *
 * Compares content, not order. Saving alphabetises the list, so between the
 * click and the refetch the edited copy is sorted while the fetched copy still
 * carries the old order; an order-sensitive comparison would flag every save as
 * an unsaved change. A reordering with no adds, removes or renames is not
 * something the admin can save.
 *
 * The element comparison itself is exact, so a case-only rename still counts as
 * a change even though `sortOptionList` orders case-insensitively.
 */
export function optionListHasUnsavedChanges(current: string[], saved: string[]): boolean {
  if (current.length !== saved.length) return true;
  const a = sortOptionList(current);
  const b = sortOptionList(saved);
  return a.some((entry, i) => entry !== b[i]);
}
